import ShortcutHelp from './ShortcutHelp';
// IDE.js
// Main IDE layout: FileExplorer + TabbedEditor
import React, { useCallback, useRef } from 'react';
import { NotificationProvider, useNotification } from './NotificationCenter.js';
import FileExplorer from './FileExplorer';
import TabbedEditor from './TabbedEditor';
import CodeSuggestionBar from './CodeSuggestionBar'; // Ensure import for hot reload
import AIAssistant from './AIAssistant';
import VoiceRecorder from './VoiceRecorder';
import CommandPalette from './CommandPalette';
import StatusBar from './StatusBar';
import GitStatusBar from './GitStatusBar';
import SplitView from './SplitView';
import ThemeSwitcher from './ThemeSwitcher';
import GitActions from './GitActions';

const IDEBody = () => {
  const tabbedEditorRef = useRef();
  const [commandPaletteOpen, setCommandPaletteOpen] = React.useState(false);
  const [aiInput, setAiInput] = React.useState('');
  const [aiProcessing, setAiProcessing] = React.useState(false);
  const [status, setStatus] = React.useState('Ready');
  const [activeFile, setActiveFile] = React.useState('');
  const [line, setLine] = React.useState(1);
  const [col, setCol] = React.useState(1);
  const [model, setModel] = React.useState('gpt-4o');
  const [split, setSplit] = React.useState(false);
  const [editorTheme, setEditorTheme] = React.useState('vs-dark');
  const [editorMinimap, setEditorMinimap] = React.useState(true);
  const [shortcutHelpOpen, setShortcutHelpOpen] = React.useState(false);
    // Keyboard shortcut for shortcut help (Ctrl+Shift+H)
    React.useEffect(() => {
      const handler = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'h') {
          setShortcutHelpOpen((v) => !v);
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, []);
  const { notify } = useNotification();
  React.useEffect(() => {
    const saveHandler = () => {
      setStatus('Saved (simulated)');
      notify('File saved!', 'success');
    };
    const paletteHandler = () => {
      setCommandPaletteOpen(true);
      notify('Command palette opened', 'info');
    };
    window.addEventListener('editor-save', saveHandler);
    window.addEventListener('editor-command-palette', paletteHandler);
    return () => {
      window.removeEventListener('editor-save', saveHandler);
      window.removeEventListener('editor-command-palette', paletteHandler);
    };
  }, [notify]);

  // Forward file open from explorer to tabbed editor
  const handleFileOpen = useCallback((filePath) => {
    if (tabbedEditorRef.current && tabbedEditorRef.current.openFile) {
      tabbedEditorRef.current.openFile(filePath);
      setActiveFile(filePath);
    }
  }, []);

  // Keyboard shortcut for command palette (Ctrl+Shift+P)
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        setCommandPaletteOpen((v) => !v);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Voice transcription handler (placeholder: just sets text)
  const handleTranscribe = async (audioBlob) => {
    setStatus('Transcribing...');
    // TODO: Send audioBlob to backend for transcription
    // For now, just simulate
    setTimeout(() => {
      setAiInput('Transcribed voice input (replace with real transcription)');
      setStatus('Ready');
    }, 1200);
  };

  return (
    <>
    <div style={{ display: 'flex', height: '100vh', width: '100vw', background: '#111', position: 'relative', boxShadow: '0 4px 32px #0004', borderRadius: 12, overflow: 'hidden' }}>
      <FileExplorer onFileOpen={handleFileOpen} />
      <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <SplitView
            left={
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#232323', padding: '4px 12px', borderBottom: '1px solid #222' }}>
                  <span style={{ color: '#b3b3b3', marginRight: 12 }}>Theme:</span>
                  <select value={editorTheme} onChange={e => setEditorTheme(e.target.value)} style={{ marginRight: 16 }}>
                    <option value="vs-dark">Dark</option>
                    <option value="vs-light">Light</option>
                    <option value="hc-black">High Contrast</option>
                  </select>
                  <label style={{ color: '#b3b3b3', marginRight: 8 }}>
                    <input type="checkbox" checked={editorMinimap} onChange={e => setEditorMinimap(e.target.checked)} /> Minimap
                  </label>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <TabbedEditor ref={tabbedEditorRef} theme={editorTheme} minimap={editorMinimap} />
                </div>
              </div>
            }
            right={
              <div style={{ width: 380, borderLeft: '1px solid #222', background: '#181818', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <AIAssistant tabbedEditorRef={tabbedEditorRef} />
                <div style={{ padding: 12, borderTop: '1px solid #222', background: '#202020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <VoiceRecorder onTranscribe={handleTranscribe} disabled={aiProcessing} />
                </div>
              </div>
            }
          />
        </div>
        <div style={{ position: 'relative' }}>
          <StatusBar status={status} file={activeFile} line={line} col={col} model={model} />
          <div style={{ position: 'absolute', right: 180, bottom: 4 }}>
            <GitStatusBar />
          </div>
        </div>
        <CommandPalette
          visible={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          onCommand={cmd => {
            setCommandPaletteOpen(false);
            if (cmd && cmd.action === 'showShortcuts') setShortcutHelpOpen(true);
          }}
          commands={[
            { label: 'Open File...', action: 'openFile' },
            { label: 'Save File', action: 'saveFile' },
            { label: 'Close Tab', action: 'closeTab' },
            { label: 'Toggle Sidebar', action: 'toggleSidebar' },
            { label: 'Go to Line...', action: 'goToLine' },
            { label: 'Open Settings', action: 'openSettings' },
            { label: 'Show Shortcuts', action: 'showShortcuts' },
            { label: 'Open Command Palette', action: 'openCommandPalette' },
          ]}
        />
        <ShortcutHelp open={shortcutHelpOpen} onClose={() => setShortcutHelpOpen(false)} />
      </div>
    </div>
    <ThemeSwitcher theme={editorTheme} setTheme={setEditorTheme} />
    <div style={{ position: 'fixed', bottom: 80, left: 32, zIndex: 1200 }}>
      <GitActions />
    </div>
    </>
  );
};

const IDE = () => (
  <NotificationProvider>
    <IDEBody />
  </NotificationProvider>
);

export default IDE;
